import { jwtDecode } from "jwt-decode";
import { APP_START_YEAR, DATE_SEPARATOR, PAYLOAD_DATA_KEY, TIME_SEPARATOR } from "../constants/commonConstants";
import { isString } from "formik";


const getPayloadData = (token) => {
  try {
    if (!token) {
      return null;
    }
    const decodedPayload = jwtDecode(token);

    const payloadDataString = decodedPayload[PAYLOAD_DATA_KEY];
    if (!payloadDataString) {
      return null;
    }
    const payloadData = JSON.parse(payloadDataString);
    return payloadData;
  } catch (error) {
    console.error("Token Error", error);
    customStorage.clear();
  }
}

const isInRole = (allowedRoles, userRole) => {
  if (!Array.isArray(allowedRoles)) {
    return false;
  }
  if (userRole == null) {
    return false;
  }
  return allowedRoles.includes(userRole);
}

const formatDateTime = (dateStr) => {
  if (!dateStr || dateStr.length < 8) {
    return null;
  }
  const year = dateStr.substring(0, 4);
  const month = dateStr.substring(4, 6);
  const day = dateStr.substring(6, 8);
  let hours = '', minutes = '', seconds = '';
  if (dateStr.length >= 12) { // YYYYMMDDHHMM
    hours = dateStr.substring(8, 10);
    minutes = dateStr.substring(10, 12);
  }
  if (dateStr.length === 14) { // YYYYMMDDHHMMSS
    seconds = dateStr.substring(12, 14);
  }
  const formattedDate = `${year}${DATE_SEPARATOR}${month}${DATE_SEPARATOR}${day}`;
  if (hours && minutes && seconds) {
    return `${formattedDate} ${hours}${TIME_SEPARATOR}${minutes}${TIME_SEPARATOR}${seconds}`;
  }
  else if (hours && minutes) {
    return `${formattedDate} ${hours}${TIME_SEPARATOR}${minutes}`;
  }
  return formattedDate;
};

const isTokenValid = (token) => {
  try {
    // Decode the JWT to get the payload
    const decodedToken = jwtDecode(token);

    // Get the expiration time (exp is in seconds)
    const expirationTime = decodedToken.exp * 1000; // Convert to milliseconds
    // Get the current time (in UTC)
    const currentTime = Date.now();
    // Check if the token has expired
    if (currentTime > expirationTime) {
      return false;
    } else {
      return true;
    }
  } catch (error) {
    return false;
  }
}

const customStorage = {
  getItem: (key) => localStorage.getItem(key),
  setItem: (key, value) => localStorage.setItem(key, value),
  removeItem: (key) => localStorage.removeItem(key),
  clear: () => { localStorage.clear(); sessionStorage.clear(); }
};

const getRoleName = (role) => {
  switch (role) {
    case 0:
      return "System Admin";
    case 1:
      return "Admin";
    default:
      return "Not Valid Role";
  }
};

const isEqualNumber = (numberOne, numberTwo) => {
  numberOne = parseInt(numberOne) || 0;
  numberTwo = parseInt(numberTwo) || 0;
  return numberOne === numberTwo;
}

const formatRoutePlaceholder = (route, params) => {
  const replacedRoute = route.replace(/{(\w+)}/g, (match, key) => {
    return params[key] || match; // Use the key to fetch the parameter value, or leave the placeholder intact if not found
  });
  return replacedRoute;
};

const isNullOrEmptyString = (str) => {
  return str === null || str === undefined || (isString(str) && str.trim() === '');
}

const isNullOrUndefinedObj = (obj) => {
  return obj === null || obj === undefined;
}

const scrollToFormError = () => {
  setTimeout(() => {
    const errorDivs = document.querySelectorAll('.formErrDiv');
    for (let errorDiv of errorDivs) {
      if (!isNullOrEmptyString(errorDiv?.innerHTML?.trim())) {
        errorDiv?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        break;
      }
    }
  }, 100);
}

const scrollToTop = (isSmooth = false) => {
  // console.log('scroll Top')
  window.scrollTo({
    top: 0,
    behavior: isSmooth ? 'smooth' : 'auto',
  });
}

const scrollToBottom = (isSmooth = false) => {
  window.scrollTo({
    top: document.documentElement.scrollHeight,
    behavior: isSmooth ? 'smooth' : 'auto',
  });
};

const getSuggestedSalePrice = (pricePerItem, percentageSettings) => {

  if (isNaN(pricePerItem) || !percentageSettings) {
    return 0;
  }

  const percentage = getTargetSuggestedProfitPercentage(pricePerItem, percentageSettings);

  let suggestedPrice = pricePerItem + (pricePerItem * percentage);
  suggestedPrice = Math.ceil(suggestedPrice / 10) * 10;

  return { suggestedPrice, percentage };
}

const getTargetSuggestedProfitPercentage = (pricePerItem, percentageSettings) => {
  let percentage = 0;
  percentageSettings?.filter(data => data.default !== true)?.forEach((range) => {
    if (pricePerItem >= range.min && pricePerItem <= range.max) {
      percentage = range.percentage;
    }
  });

  if (percentage === 0) {
    const defaultData = percentageSettings?.find(data => data.default === true);
    if (pricePerItem >= defaultData?.min) {
      percentage = defaultData?.percentage || 0
    }
  }

  return percentage;
}

const formatNumberPadding = (num, padding = 2) => String(num).padStart(padding, '0');

const sortMixedArray = (arr, isDesc = false) => {
  if (!Array.isArray(arr)) {
    return arr;
  }

  const order = isDesc ? "desc" : "asc";

  return arr.sort((a, b) => {
    const strA = String(a); // Convert to string
    const strB = String(b); // Convert to string

    const numA = parseInt(strA);
    const numB = parseInt(strB);
    const isNumA = !isNaN(numA);
    const isNumB = !isNaN(numB);

    if (isNumA && isNumB) {
      // Both are numbers, sort numerically
      return order === "asc" ? numA - numB : numB - numA;
    } else if (!isNumA && !isNumB) {
      // Both are non-numeric, sort alphabetically
      return order === "asc" ? strA.localeCompare(strB) : strB.localeCompare(strA);
    } else {
      // One is numeric, the other is not → numbers should come first
      return isNumA ? -1 : 1;
    }
  });
};

const getChangingSelectColor = (before, after) => {
  before = before?.toString() || "";
  after = after?.toString() || "";

  return before === after ? "" : "changing-select-input";
}

const getNextYearMonth = (currentYear, currentMonth) => {
  // month is 1-based (1 = January, 12 = December)
  let nextMonth = currentMonth + 1;
  let nextYear = currentYear;

  if (nextMonth > 12) {
    nextMonth = 1;
    nextYear += 1;
  }

  return { year: nextYear, month: nextMonth };
}

const getInitialAndCurrentYearMonth = (previousMonthCount = 0, isNotIncludeCurrentMonth = false) => {
  const jst = getCurrentJSTDate();

  if (isNotIncludeCurrentMonth) {
    previousMonthCount = previousMonthCount + 1;
  }

  // Calculate fromYear and fromMonth (X months ago)
  const tempDate = new Date(jst.getFullYear(), jst.getMonth() - parseInt(previousMonthCount || 0), 1);
  const fromYearInitial = tempDate.getFullYear();
  const fromMonthInitial = tempDate.getMonth() + 1; // 1-based month

  let toYear = jst.getFullYear();
  let toMonth = jst.getMonth() + 1; // 1-based

  // Adjust if current month is NOT to be included
  if (isNotIncludeCurrentMonth) {
    toMonth -= 1;
    if (toMonth === 0) {
      toMonth = 12;
      toYear -= 1;
    }
  }

  return {
    fromYear: fromYearInitial,
    fromMonth: fromMonthInitial,
    toYear,
    toMonth
  };
};

const isValidHttpUrl = (urlString) => {
  try {
    const url = new URL(urlString);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch (_) {
    return false;
  }
}

const padNumber = (numStr, padCount) => String(numStr).padStart(padCount, '0');

const getCurrentJSTDate = () => {
  const now = new Date();
  const jst = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Tokyo" }));
  return jst;
};

const getCurrentJSTString = () => {
  const jst = getCurrentJSTDate();


  const jstString = (
    jst.getFullYear().toString() +
    padNumber(jst.getMonth() + 1, 2) +
    padNumber(jst.getDate(), 2) +
    padNumber(jst.getHours(), 2) +
    padNumber(jst.getMinutes(), 2) +
    padNumber(jst.getSeconds(), 2)
  );
  return jstString;
};

const currentYear = getCurrentJSTDate().getFullYear();
const currentMonth = getCurrentJSTDate().getMonth() + 1;
const years = Array.from({ length: (currentYear - APP_START_YEAR) + 1}, (_, i) => currentYear - i);
const months = Array.from({ length: 12 }, (_, i) => i + 1);

// Get available months based on selected year
const getAvailableMonths = (year) => {
  return months.filter((month) => year < currentYear || month <= currentMonth);
};

// Handle From Year Change
const handleFromYearChange = (e, fromMonth, toYear, toMonth, setFromYear, setFromMonth, setToYear, setToMonth) => {
  const newFromYear = parseInt(e.target.value);
  if (newFromYear > toYear) {
    setToYear(newFromYear);
    setToMonth(fromMonth);
  } else if (newFromYear === parseInt(toYear) && parseInt(fromMonth) > parseInt(toMonth)) {
    setFromMonth(1);
  }
  setFromYear(newFromYear);
};

// Handle From Month Change
const handleFromMonthChange = (e, fromYear, toYear, toMonth, setFromMonth, setToMonth) => {
  const newFromMonth = parseInt(e.target.value);
  if (parseInt(fromYear) === parseInt(toYear) && newFromMonth > parseInt(toMonth)) {
    setToMonth(newFromMonth);
  }
  setFromMonth(newFromMonth);
};

// Handle To Year Change
const handleToYearChange = (e, fromYear, fromMonth, toMonth, setFromYear, setToYear, setFromMonth) => {
  const newToYear = parseInt(e.target.value);
  if (newToYear < parseInt(fromYear)) {
    setFromYear(newToYear);
    setFromMonth(parseInt(toMonth));
  } else if (newToYear === parseInt(fromYear) && parseInt(toMonth) < parseInt(fromMonth)) {
    setFromMonth(parseInt(toMonth));
  }
  setToYear(newToYear);
};

// Handle To Month Change
const handleToMonthChange = (e, fromYear, fromMonth, toYear, setFromMonth, setToMonth) => {
  const newToMonth = parseInt(e.target.value);
  if (parseInt(toYear) === parseInt(fromYear) && newToMonth < parseInt(fromMonth)) {
    setFromMonth(newToMonth);
  }
  setToMonth(newToMonth);
};

export {
  formatDateTime, getPayloadData, isInRole, isTokenValid, customStorage, getRoleName, formatNumberPadding,
  formatRoutePlaceholder, isNullOrEmptyString, isNullOrUndefinedObj, scrollToFormError, getSuggestedSalePrice, getTargetSuggestedProfitPercentage, scrollToTop,
  sortMixedArray, scrollToBottom, isEqualNumber, getChangingSelectColor, getNextYearMonth, getInitialAndCurrentYearMonth,
  isValidHttpUrl, getCurrentJSTString, handleFromYearChange, handleFromMonthChange, handleToYearChange, handleToMonthChange, getAvailableMonths, years,
  months, currentYear, currentMonth, padNumber, getCurrentJSTDate
}
